import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  useMediaQuery,
} from "@mui/material";
import { dt } from "../../themes/redesignTokens";
import { useRedesignSetup } from "../../hooks/useRedesignSetup";

type ConfirmationDialogProps = {
  open: boolean;
  title?: string;
  text?: string;
  confirmButton?: string;
  closeButton?: string;
  severity?: "neutral" | "error";
  onConfirm?: () => void;
  onClose?: () => void;
};

/* -- Severity color maps -- */
const severityColors = {
  neutral: {
    gradient: `linear-gradient(90deg, ${dt.blue} 0%, #5DADE2 50%, ${dt.blue}00 100%)`,
    buttonGradient: `linear-gradient(135deg, ${dt.blue} 0%, #5DADE2 100%)`,
    buttonGradientHover: `linear-gradient(135deg, #5DADE2 0%, ${dt.blue} 100%)`,
    shadow: "0 2px 8px rgba(52,152,219,0.25)",
    shadowHover: "0 6px 20px rgba(52,152,219,0.3)",
  },
  error: {
    gradient: `linear-gradient(90deg, #E74C3C 0%, #F06292 50%, #E74C3C00 100%)`,
    buttonGradient: `linear-gradient(135deg, #E74C3C 0%, #EF5350 100%)`,
    buttonGradientHover: `linear-gradient(135deg, #EF5350 0%, #E74C3C 100%)`,
    shadow: "0 2px 8px rgba(231,76,60,0.25)",
    shadowHover: "0 6px 20px rgba(231,76,60,0.3)",
  },
};

const ConfirmationDialog = ({
  open,
  title,
  text,
  confirmButton = "Confirm",
  closeButton = "Cancel",
  severity = "neutral",
  onConfirm = () => {},
  onClose = () => {},
}: ConfirmationDialogProps) => {
  useRedesignSetup();
  const isPhone = useMediaQuery("(max-width:600px)");
  const colors = severityColors[severity];

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "confirmation-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes confirmationDialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          fontFamily: dt.font,
          width: "450px",
          maxWidth: "calc(100% - 32px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "confirmationDialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
          }),
        },
      }}
    >
      {/* Gradient accent bar */}
      <Box
        sx={{
          height: "4px",
          background: colors.gradient,
        }}
      />

      {/* Title */}
      <DialogTitle
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          pt: 3,
          pb: 0.5,
          px: 3,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: { xs: "18px", sm: "20px" },
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
            lineHeight: 1.3,
          }}
        >
          {title}
        </Box>
      </DialogTitle>

      {/* Body text */}
      <DialogContent sx={{ px: 3, pt: 0.5, pb: 0 }}>
        <Box
          sx={{
            fontSize: "14px",
            color: dt.textSecondary,
            textAlign: "center",
            lineHeight: 1.6,
            wordBreak: "break-word",
          }}
        >
          {text}
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 2,
          gap: 1,
          justifyContent: "center",
          flexDirection: isPhone ? "column-reverse" : "row",
          "& > :not(:first-of-type)": {
            ml: isPhone ? "0 !important" : undefined,
          },
        }}
      >
        {/* Cancel — text button */}
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            width: isPhone ? "100%" : "auto",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          {closeButton}
        </Button>

        {/* Confirm — gradient pill */}
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            background: colors.buttonGradient,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            py: 1,
            boxShadow: colors.shadow,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              background: colors.buttonGradientHover,
              transform: "translateY(-2px)",
              boxShadow: colors.shadowHover,
            },
          }}
        >
          {confirmButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ConfirmationDialog;
